import {
  useQuery,
  type QueryKey,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query'
import { useAuthStore } from '../store/authStore'

/**
 * A read whose data comes from Microsoft Graph / Intune.
 *
 * Those calls take seconds, so the cached response is rendered immediately and the fresh one
 * replaces it when it lands. Pair the query with `<SyncStatus>`: the page shows data that may be
 * a minute old, and nothing else on screen says so.
 */

/** How long a response is served without a refetch. Under this, revisiting a page costs nothing. */
export const INTUNE_STALE_TIME = 60_000

/** How long a response is kept after the last component reading it unmounts. The default 5 minutes
 *  drops the cache while the user is still in the same session, which is the case this exists for. */
export const INTUNE_GC_TIME = 30 * 60_000

export function useIntuneQuery<TData>(
  options: UseQueryOptions<TData, Error, TData, QueryKey> & { queryKey: QueryKey },
): UseQueryResult<TData, Error> {
  const tenantId = useAuthStore((s) => s.tenantContext?.tenant_id ?? null)
  return useQuery({
    staleTime: INTUNE_STALE_TIME,
    gcTime: INTUNE_GC_TIME,
    ...options,
    // The tenant goes last so that an `invalidateQueries({ queryKey: ['deployments'] })` elsewhere
    // still matches this entry by prefix. Without it one tenant's cache would be served to the
    // next one the moment the cache outlives a tenant switch.
    queryKey: [...options.queryKey, { tenant: tenantId }],
  })
}

/**
 * The cache key for each Graph resource, in one place.
 *
 * A resource read from two files under two spellings is fetched twice and refreshed once, which
 * is how `ad-groups` / `azure-ad-groups` and `assignments` / `deployment-assignments` each came to
 * hold the same data under two keys. Everything reading Graph takes its key from here.
 */
export const intuneKeys = {
  /** Azure AD security groups, optionally narrowed by the picker's search box. */
  adGroups: (search?: string) => ['azure-ad-groups', search || ''] as const,
  assignmentFilters: () => ['assignment-filters'] as const,
  /** Company Portal categories for this tenant. */
  appCategories: () => ['intune-app-categories'] as const,
  /** An app's assignments. Two Graph round-trips — the assignments, then the groups that name
   *  their targets — so this is the most expensive read on the page. */
  assignments: (appId: string | null | undefined) => ['assignments', appId] as const,
  deployments: () => ['deployments'] as const,
  deploymentDetails: (deploymentId: string | null | undefined) =>
    ['deployment-details', deploymentId] as const,
  /** Per-device install status. Its own key because it is its own request: the report export
   *  behind it is far slower than the deployment it belongs to. */
  deviceStatus: (deploymentId: string | null | undefined) =>
    ['deployment-device-status', deploymentId] as const,
  /** A branding rollout, which reads its audience back from Intune. */
  brandingDeployment: (deploymentId: string | null | undefined) =>
    ['branding-deployment', deploymentId] as const,
}
